"use client";

import { createContext, useContext, ReactNode } from "react";
import { type Store } from "@/actions/schemas/stores";
import { useSetAmplitudeStoreId } from "@/contexts/analitica";

type StoreWithWhatsAppEnabled = Store & {
  vitrine: Store["vitrine"] & {
    whatsAppNumber: string;
    whatsAppSharingEnabled: true;
  };
};

type StoreProviderProps = {
  store: Store;
  children: ReactNode;
};

export type StoreContextType =
  | {
      store: Store;
      enabledWhatsAppSharing: false;
    }
  | {
      store: StoreWithWhatsAppEnabled;
      enabledWhatsAppSharing: true;
    };

function hasEnabledWhatsApp(store: Store): store is StoreWithWhatsAppEnabled {
  return !!(
    store.vitrine.whatsAppSharingEnabled && store.vitrine.whatsAppNumber
  );
}

const StoreContext = createContext<StoreContextType>({} as StoreContextType);

export const StoreProvider = ({ store, children }: StoreProviderProps) => {
  const storeName = store.vitrine.name || store.name;
  store = { ...store, name: storeName };

  useSetAmplitudeStoreId(store.id);

  if (hasEnabledWhatsApp(store)) {
    return (
      <StoreContext.Provider value={{ store, enabledWhatsAppSharing: true }}>
        {children}
      </StoreContext.Provider>
    );
  }

  return (
    <StoreContext.Provider value={{ store, enabledWhatsAppSharing: false }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);

  if (!context.store) {
    throw new Error("useStore must be used within a StoreProvider");
  }

  return context;
};
